<template>
  <div class="checkbox-payment-mappings-tab">
    <div class="tab-toolbar">
      <b-button size="sm" variant="primary" @click="openAddModal">
        <i class="fa fa-plus"></i>
        Додати маппінг
      </b-button>
      <b-button size="sm" variant="outline-secondary" @click="refresh" :disabled="isLoading" class="ml-2">
        <i class="fa" :class="isLoading ? 'fa-spinner fa-spin' : 'fa-refresh'"></i>
        Оновити
      </b-button>
    </div>

    <div v-if="isLoading && !hasLoaded" class="tab-loading text-center text-muted py-4">
      <i class="fa fa-spinner fa-spin mr-1"></i>
      Завантаження...
    </div>

    <div v-else class="tab-content">
      <table class="table table-sm table-bordered table-hover">
        <thead>
          <tr>
            <th style="width: 50px">ID</th>
            <th>Метод оплати CRM</th>
            <th>Тип оплати Checkbox</th>
            <th>Мітка чеку</th>
            <th style="width: 120px">Дії</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in items" :key="item.id">
            <td>{{ item.id }}</td>
            <td>{{ item.payment_method_title }}</td>
            <td>
              <span class="badge badge-info">
                {{ paymentTypeLabel(item.checkbox_payment_type) }}
                <span v-if="item.integrator_name"> ({{ item.integrator_name }})</span>
              </span>
            </td>
            <td>
              <span v-if="item.checkbox_payment_label" class="label-text">{{ item.checkbox_payment_label }}</span>
              <span v-else class="text-muted">—</span>
            </td>
            <td class="actions-cell">
              <b-button variant="link" size="sm" @click="openEditModal(item)" title="Редагувати">
                <i class="fa fa-edit"></i>
              </b-button>
              <b-button variant="link" size="sm" class="text-danger" @click="confirmDelete(item)" title="Видалити">
                <i class="fa fa-trash"></i>
              </b-button>
            </td>
          </tr>
          <tr v-if="!items.length && hasLoaded">
            <td colspan="5" class="text-center text-muted py-3">
              Немає маппінгів. Натисніть "Додати маппінг" щоб створити новий.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Add/Edit Modal -->
    <div v-if="showModal" class="mapping-modal-overlay" @click.self="closeModal">
      <div class="mapping-modal" @click.stop>
        <div class="mapping-modal__header">
          <h5>{{ isEditMode ? 'Редагування маппінгу' : 'Новий маппінг оплати' }}</h5>
          <button type="button" class="close-btn" @click="closeModal">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="mapping-modal__body">
          <b-form-group label="Метод оплати CRM" label-cols-sm="5" label-size="sm" class="mb-3">
            <v-select
              v-model="modalForm.payment_method_id"
              :options="crmMethodsList"
              :reduce="i => i.id"
              label="title"
              placeholder="Оберіть метод оплати"
              :class="{ 'is-invalid': modalErrors.payment_method_id }"
            />
            <div v-if="modalErrors.payment_method_id" class="invalid-feedback d-block">
              {{ modalErrors.payment_method_id }}
            </div>
          </b-form-group>

          <b-form-group label="Тип оплати Checkbox" label-cols-sm="5" label-size="sm" class="mb-3">
            <v-select
              v-model="modalForm.checkbox_payment_type"
              :options="checkboxTypesList"
              :reduce="i => i.value"
              label="label"
              placeholder="Оберіть тип оплати"
              :class="{ 'is-invalid': modalErrors.checkbox_payment_type }"
              @input="onPaymentTypeChange"
            />
            <div v-if="modalErrors.checkbox_payment_type" class="invalid-feedback d-block">
              {{ modalErrors.checkbox_payment_type }}
            </div>
          </b-form-group>

          <b-form-group
            v-if="modalForm.checkbox_payment_type === 'CASHLESS' && cashlessPaymentOptions.length > 0"
            label="Інтегратор оплати"
            label-cols-sm="5"
            label-size="sm"
            class="mb-3"
          >
            <v-select
              v-model="selectedIntegrator"
              :options="cashlessPaymentOptions"
              :reduce="i => i.name"
              label="label"
              placeholder="Оберіть інтегратора"
              @input="onIntegratorSelect"
            />
            <small class="form-text text-muted">
              Виберіть інтегратора для автоматичного заповнення мітки чеку
            </small>
          </b-form-group>

          <b-form-group label="Мітка для чеку" label-cols-sm="5" label-size="sm" class="mb-3">
            <b-form-input
              v-model="modalForm.checkbox_payment_label"
              placeholder="Мітка що друкується на чеку"
              maxlength="128"
              :class="{ 'is-invalid': modalErrors.checkbox_payment_label }"
            />
            <div v-if="modalErrors.checkbox_payment_label" class="invalid-feedback d-block">
              {{ modalErrors.checkbox_payment_label }}
            </div>
            <small class="form-text text-muted">
              Максимум 128 символів. Обов'язково для інтеграторів.
            </small>
          </b-form-group>
        </div>
        <div class="mapping-modal__footer">
          <b-button variant="secondary" size="sm" @click="closeModal" class="mr-2">
            Скасувати
          </b-button>
          <b-button variant="primary" size="sm" @click="saveModal" :disabled="isSaving">
            <i v-if="isSaving" class="fa fa-spinner fa-spin mr-1"></i>
            {{ isEditMode ? 'Зберегти' : 'Додати' }}
          </b-button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteConfirm" class="mapping-modal-overlay" @click.self="showDeleteConfirm = false">
      <div class="mapping-modal mapping-modal--small" @click.stop>
        <div class="mapping-modal__header">
          <h5>Підтвердження видалення</h5>
          <button type="button" class="close-btn" @click="showDeleteConfirm = false">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="mapping-modal__body text-center">
          <p>Ви впевнені, що хочете видалити цей маппінг оплати?</p>
        </div>
        <div class="mapping-modal__footer justify-content-center">
          <b-button variant="secondary" size="sm" @click="showDeleteConfirm = false" class="mr-2">
            Скасувати
          </b-button>
          <b-button variant="danger" size="sm" @click="deleteItem" :disabled="isDeleting">
            <i v-if="isDeleting" class="fa fa-spinner fa-spin mr-1"></i>
            Видалити
          </b-button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'PaymentMappingsTab',
  props: {
    active: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: false,
      hasLoaded: false,
      isSaving: false,
      isDeleting: false,
      items: [],
      crmMethodsList: [],
      checkboxTypesList: [],
      paymentOptionsList: [],
      selectedIntegrator: null,
      showModal: false,
      isEditMode: false,
      editingItem: null,
      modalForm: {
        payment_method_id: null,
        checkbox_payment_type: '',
        checkbox_payment_label: '',
        integrator_name: null,
      },
      modalErrors: {},
      showDeleteConfirm: false,
      deletingItem: null,
    };
  },
  computed: {
    ...mapGetters({
      paymentMappings: 'checkbox/paymentMappings',
      checkboxPaymentTypes: 'checkbox/checkboxPaymentTypes',
      crmPaymentMethods: 'checkbox/crmPaymentMethods',
      paymentOptions: 'checkbox/paymentOptions',
    }),
    cashlessPaymentOptions() {
      return (this.paymentOptionsList || []).filter(option => option.type === 'CASHLESS');
    },
  },
  watch: {
    active: {
      immediate: true,
      handler(val) {
        if (val && !this.hasLoaded) {
          this.loadData();
        }
      },
    },
    paymentMappings: {
      immediate: true,
      handler(data) {
        this.items = [];
        if (data && Array.isArray(data)) {
          data.forEach(item => {
            this.items.push({
              id: item.id,
              payment_method_id: item.payment_method_id,
              payment_method_title: item.payment_method ? item.payment_method.title : '—',
              checkbox_payment_type: item.checkbox_payment_type,
              checkbox_payment_label: item.checkbox_payment_label || '',
              integrator_name: item.integrator_name || null,
              _raw: item,
            });
          });
        }
      },
    },
    checkboxPaymentTypes: {
      immediate: true,
      handler(data) {
        if (data && data.length) {
          this.checkboxTypesList = data;
        } else {
          // Fallback to hardcoded defaults if empty
          this.checkboxTypesList = [
            { value: 'CASH', label: 'Готівка (CASH)' },
            { value: 'CASHLESS', label: 'Безготівка (CASHLESS)' }
          ];
        }
      },
    },
    crmPaymentMethods: {
      immediate: true,
      handler(data) {
        this.crmMethodsList = data || [];
      },
    },
    paymentOptions: {
      immediate: true,
      handler(data) {
        this.paymentOptionsList = data || [];
        this.syncSelectedIntegrator();
      },
    },
    'modalForm.checkbox_payment_label'() {
      this.syncSelectedIntegrator();
    },
    'modalForm.checkbox_payment_type'() {
      this.syncSelectedIntegrator();
    },
  },
  methods: {
    paymentTypeLabel(value) {
      if (value === 'CASH') return 'Готівка';
      if (value === 'CASHLESS') return 'Безготівковий';
      return value || '—';
    },
    syncSelectedIntegrator() {
      if (this.modalForm.checkbox_payment_type !== 'CASHLESS') {
        this.selectedIntegrator = null;
        this.modalForm.integrator_name = null;
        return;
      }
      if (!this.cashlessPaymentOptions.length) {
        return;
      }
      if (this.modalForm.integrator_name) {
        const matchByName = this.cashlessPaymentOptions.find(option => option.name === this.modalForm.integrator_name);
        if (matchByName) {
          this.selectedIntegrator = matchByName.name;
          return;
        }
      }
      if (!this.modalForm.checkbox_payment_label) {
        return;
      }
      const matchByLabel = this.cashlessPaymentOptions.find(option => option.label === this.modalForm.checkbox_payment_label);
      if (matchByLabel) {
        this.selectedIntegrator = matchByLabel.name;
        this.modalForm.integrator_name = matchByLabel.name;
      }
    },
    async loadData() {
      this.isLoading = true;
      try {
        await Promise.all([
          this.getPaymentMappings(),
          this.getCheckboxPaymentTypes(),
          this.getCrmPaymentMethods(),
          this.getPaymentOptions(),
        ]);
        // Ensure checkboxTypesList has fallback defaults
        if (!this.checkboxTypesList || this.checkboxTypesList.length === 0) {
          this.checkboxTypesList = [
            { value: 'CASH', label: 'Готівка (CASH)' },
            { value: 'CASHLESS', label: 'Безготівка (CASHLESS)' }
          ];
        }
      } catch (e) {
        this.$toasted.error('Помилка завантаження даних', { duration: 3000 });
      } finally {
        this.isLoading = false;
      }
    },
    async refresh() {
      await this.loadData();
    },
    openAddModal() {
      this.isEditMode = false;
      this.editingItem = null;
      this.selectedIntegrator = null;
      this.modalForm = {
        payment_method_id: null,
        checkbox_payment_type: 'CASHLESS',
        checkbox_payment_label: '',
        integrator_name: null,
      };
      this.modalErrors = {};
      this.showModal = true;
    },
    openEditModal(item) {
      this.isEditMode = true;
      this.editingItem = item;
      this.selectedIntegrator = null;
      this.modalForm = {
        payment_method_id: item.payment_method_id,
        checkbox_payment_type: item.checkbox_payment_type,
        checkbox_payment_label: item.checkbox_payment_label || '',
        integrator_name: item.integrator_name || null,
      };
      this.selectedIntegrator = this.modalForm.integrator_name || null;
      this.modalErrors = {};
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.editingItem = null;
      this.modalErrors = {};
      this.selectedIntegrator = null;
    },
    onPaymentTypeChange(value) {
      this.selectedIntegrator = null;
      this.modalForm.integrator_name = null;
      if (value !== 'CASHLESS') {
        this.modalForm.checkbox_payment_label = '';
      }
    },
    onIntegratorSelect(name) {
      if (name) {
        const option = this.cashlessPaymentOptions.find(o => o.name === name);
        if (option) {
          this.modalForm.checkbox_payment_label = option.label;
          this.modalForm.integrator_name = option.name;
        }
      }
    },
    validateModal() {
      this.modalErrors = {};
      if (!this.modalForm.payment_method_id) {
        this.modalErrors.payment_method_id = 'Оберіть метод оплати';
      }
      if (!this.modalForm.checkbox_payment_type) {
        this.modalErrors.checkbox_payment_type = 'Оберіть тип оплати';
      }
      if (this.modalForm.checkbox_payment_type === 'CASHLESS' && !this.modalForm.checkbox_payment_label) {
        this.modalErrors.checkbox_payment_label = 'Вкажіть мітку для чеку';
      }
      return Object.keys(this.modalErrors).length === 0;
    },
    async saveModal() {
      if (!this.validateModal()) {
        return;
      }
      this.isSaving = true;
      try {
        const payload = { ...this.modalForm };
        if (this.isEditMode) {
          const res = await this.updatePaymentMapping({ id: this.editingItem.id, data: payload });
          if (res.status) {
            this.$toasted.success('Маппінг оновлено', { duration: 3000 });
            this.closeModal();
            await this.refresh();
          } else {
            this.$toasted.error((res.data && res.data.message) || 'Помилка оновлення', { duration: 3000 });
          }
        } else {
          const res = await this.createPaymentMapping(payload);
          if (res.status) {
            this.$toasted.success('Маппінг створено', { duration: 3000 });
            this.closeModal();
            await this.refresh();
          } else {
            this.$toasted.error((res.data && res.data.message) || 'Помилка створення', { duration: 3000 });
          }
        }
      } catch (e) {
        this.$toasted.error('Помилка збереження', { duration: 3000 });
      } finally {
        this.isSaving = false;
      }
    },
    confirmDelete(item) {
      this.deletingItem = item;
      this.showDeleteConfirm = true;
    },
    async deleteItem() {
      if (!this.deletingItem) return;
      this.isDeleting = true;
      try {
        const res = await this.deletePaymentMapping(this.deletingItem.id);
        if (res.status) {
          this.$toasted.success('Маппінг видалено', { duration: 3000 });
          this.showDeleteConfirm = false;
          this.deletingItem = null;
          await this.refresh();
        } else {
          this.$toasted.error((res.data && res.data.message) || 'Помилка видалення', { duration: 3000 });
        }
      } catch (e) {
        this.$toasted.error('Помилка видалення', { duration: 3000 });
      } finally {
        this.isDeleting = false;
      }
    },
    ...mapActions({
      getPaymentMappings: 'checkbox/getPaymentMappings',
      getCheckboxPaymentTypes: 'checkbox/getCheckboxPaymentTypes',
      getCrmPaymentMethods: 'checkbox/getCrmPaymentMethods',
      getPaymentOptions: 'checkbox/getPaymentOptions',
      createPaymentMapping: 'checkbox/createPaymentMapping',
      updatePaymentMapping: 'checkbox/updatePaymentMapping',
      deletePaymentMapping: 'checkbox/deletePaymentMapping',
    }),
  },
};
</script>

<style lang="scss" scoped>
.checkbox-payment-mappings-tab {
  padding: 15px;
  min-height: 300px;
}

.tab-toolbar {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}

.tab-content {
  .table {
    margin-bottom: 0;
    font-size: 13px;

    th {
      background: #f8f9fa;
      font-weight: 600;
      white-space: nowrap;
    }

    td {
      vertical-align: middle;
    }
  }

  .actions-cell {
    white-space: nowrap;
    text-align: center;

    .btn-link {
      padding: 2px 6px;
    }
  }

  .badge-info {
    font-size: 12px;
    font-weight: 500;
  }

  .label-text {
    font-size: 12px;
    color: #495057;
    max-width: 200px;
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.mapping-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1060;
}

.mapping-modal {
  background: #fff;
  border-radius: 8px;
  width: 500px;
  max-width: 90vw;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);

  &--small {
    width: 400px;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid #dee2e6;
    background: #f8f9fa;

    h5 {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
    }

    .close-btn {
      border: none;
      background: none;
      font-size: 18px;
      color: #6c757d;
      cursor: pointer;
      padding: 0;
      line-height: 1;

      &:hover {
        color: #343a40;
      }
    }
  }

  &__body {
    padding: 16px;
    overflow-y: auto;
    max-height: calc(90vh - 120px);
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    padding: 12px 16px;
    border-top: 1px solid #dee2e6;
    background: #f8f9fa;
  }
}

::v-deep .form-group {
  label {
    text-align: right;
    padding-right: 1rem;
    font-size: 13px;
  }
}

::v-deep .v-select {
  &.is-invalid .vs__dropdown-toggle {
    border-color: #dc3545;
  }

  .vs__dropdown-toggle {
    min-height: 31px;
  }
}
</style>
